import { useCallback, useEffect, useRef, useState } from 'react';
import { IDE_SNIPPETS } from './snippets';
import { useReveal } from '../../hooks/useReveal';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const u = () => setReduced(mq.matches);
    u();
    mq.addEventListener('change', u);
    return () => mq.removeEventListener('change', u);
  }, []);
  return reduced;
}

function tokenUnitsTotal(lines) {
  return lines.reduce((n, row) => n + row.length, 0);
}

const LOOP_PAUSE_MS = 2400;

export default function IdeCodePanel({
  variant,
  caption,
  className = '',
  flip = false,
}) {
  const config = IDE_SNIPPETS[variant];
  const [wrapRef, visible] = useReveal({ threshold: 0.08 });
  const reducedMotion = usePrefersReducedMotion();
  const [visibleUnits, setVisibleUnits] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const timerRef = useRef(null);
  const loopTimeoutRef = useRef(null);
  const startedRef = useRef(false);

  const clearLoopTimeout = useCallback(() => {
    if (loopTimeoutRef.current) {
      window.clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }
  }, []);

  const isTerminal = config.mode === 'terminal';
  const totalUnits = tokenUnitsTotal(config.lines);
  const totalLines = config.lines.length;

  const resetAndStart = useCallback(() => {
    if (isTerminal) {
      setVisibleLines(0);
    } else {
      setVisibleUnits(0);
    }
  }, [isTerminal]);

  const runTimer = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    const interval = config.intervalMs ?? 40;
    if (isTerminal) {
      timerRef.current = window.setInterval(() => {
        setVisibleLines((v) => {
          if (v >= totalLines) {
            if (timerRef.current) window.clearInterval(timerRef.current);
            return v;
          }
          return v + 1;
        });
      }, Math.max(interval, 120));
    } else {
      timerRef.current = window.setInterval(() => {
        setVisibleUnits((v) => {
          if (v >= totalUnits) {
            if (timerRef.current) window.clearInterval(timerRef.current);
            return v;
          }
          return v + 1;
        });
      }, interval);
    }
  }, [config.intervalMs, isTerminal, totalLines, totalUnits]);

  useEffect(() => {
    if (!visible) {
      startedRef.current = false;
      clearLoopTimeout();
      if (timerRef.current) window.clearInterval(timerRef.current);
      return undefined;
    }

    if (reducedMotion) {
      setVisibleUnits(totalUnits);
      setVisibleLines(totalLines);
      return undefined;
    }

    if (startedRef.current) return undefined;
    startedRef.current = true;
    resetAndStart();
    runTimer();

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      clearLoopTimeout();
    };
  }, [visible, reducedMotion, isTerminal, totalUnits, totalLines, resetAndStart, runTimer, clearLoopTimeout]);

  const done = isTerminal ? visibleLines >= totalLines : visibleUnits >= totalUnits;

  useEffect(() => {
    if (!visible || reducedMotion || !done) {
      clearLoopTimeout();
      return undefined;
    }

    loopTimeoutRef.current = window.setTimeout(() => {
      loopTimeoutRef.current = null;
      if (timerRef.current) window.clearInterval(timerRef.current);
      resetAndStart();
      runTimer();
    }, LOOP_PAUSE_MS);

    return () => clearLoopTimeout();
  }, [done, visible, reducedMotion, resetAndStart, runTimer, clearLoopTimeout]);

  return (
    <div
      ref={wrapRef}
      className={`ca-ide-showcase ${flip ? 'ca-ide-showcase--flip' : ''} ${className}`.trim()}
    >
      <div className="ca-ide-showcase__meta">
        {caption ? <p className="ca-ide-showcase__caption">{caption}</p> : null}
      </div>

      <div
        className={`ca-ide ${isTerminal ? 'ca-ide--terminal' : ''}`}
        role="img"
        aria-label={caption || 'Animated IDE code sample that loops automatically'}
      >
        <div className="ca-ide__titlebar">
          <span className="ca-ide__dots" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <span className="ca-ide__title">{config.title}</span>
        </div>

        {!isTerminal ? (
          <div className="ca-ide__tabs" role="tablist" aria-hidden>
            {config.tabs.map((tab, i) => (
              <span
                key={tab}
                className={`ca-ide__tab ${i === config.activeTab ? 'ca-ide__tab--active' : ''}`}
              >
                {tab}
              </span>
            ))}
          </div>
        ) : null}

        <div className="ca-ide__body">
          {!isTerminal ? (
            <div className="ca-ide__activity" aria-hidden>
              <span className="ca-ide__act ca-ide__act--on" />
              <span className="ca-ide__act" />
              <span className="ca-ide__act" />
              <span className="ca-ide__act" />
              <span className="ca-ide__act" />
            </div>
          ) : null}

          <div className="ca-ide__editor">
            <pre className="ca-ide__pre">
              {config.lines.map((tokens, li) => {
                if (isTerminal) {
                  const shown = li < visibleLines;
                  return (
                    <div key={li} className="ca-ide__line" aria-hidden={!shown}>
                      <span className="ca-ide__ln ca-ide__ln--term" />
                      <code className="ca-ide__code">
                        {tokens.map((tok, ti) => (
                          <span
                            key={ti}
                            className={`ca-ide-tok ca-ide-tok--${tok.c} ${shown ? '' : 'ca-ide-tok--terminal-wait'}`}
                          >
                            {tok.t}
                          </span>
                        ))}
                      </code>
                    </div>
                  );
                }

                const prefix = config.lines.slice(0, li).reduce((s, r) => s + r.length, 0);
                const shown = Math.max(0, Math.min(tokens.length, visibleUnits - prefix));
                const row = tokens.slice(0, shown);
                const lineComplete = tokens.length === 0 || shown === tokens.length;
                const isHl = config.highlightLineIndex === li && lineComplete;
                const typingHere =
                  !done && tokens.length > 0 && visibleUnits > prefix && visibleUnits < prefix + tokens.length;

                return (
                  <div
                    key={li}
                    className={`ca-ide__line ${isHl ? 'ca-ide__line--hl' : ''}`}
                  >
                    <span className="ca-ide__ln">{config.lineStart + li}</span>
                    <code className="ca-ide__code">
                      {row.map((tok, ti) => (
                        <span key={ti} className={`ca-ide-tok ca-ide-tok--${tok.c}`}>
                          {tok.t}
                        </span>
                      ))}
                      {typingHere ? <span className="ca-ide__caret" /> : null}
                    </code>
                  </div>
                );
              })}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
